import React, { useState, useEffect } from 'react';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Award,
  Users,
  Calendar,
  Package,
  Target,
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
} from 'recharts';
import { getApiUrl, getAuthHeaders } from '../utils/auth';
import { useTheme } from '../context/ThemeContext';
import StyledDropdown from './StyledDropdown';

const UserAnalytics = () => {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [overview, setOverview] = useState(null);
  const [salesTrend, setSalesTrend] = useState([]);
  const [leaderboard, setLeaderboard] = useState(null);
  const [salesByProduct, setSalesByProduct] = useState([]);

  // Chart colors based on theme
  const axisColor = isDarkMode ? '#94a3b8' : '#6b7280';
  const gridColor = isDarkMode ? '#334155' : '#f0f0f0';

  // Filters
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 2020 + 2 }, (_, i) => currentYear + 1 - i);
  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'];

  const formatCurrency = (amount) => {
    if (amount >= 1000000) {
      return `RM ${(amount / 1000000).toFixed(2)}M`;
    } else if (amount >= 1000) {
      return `RM ${(amount / 1000).toFixed(1)}K`;
    }
    return `RM ${amount.toFixed(2)}`;
  };

  const formatCurrencyFull = (amount) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR',
    }).format(amount);
  };

  // Fetch all data
  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');

      const [overviewRes, trendRes, leaderboardRes, productRes] = await Promise.all([
        fetch(`${getApiUrl()}/api/analytics/user/overview?year=${filterYear}`, { headers: getAuthHeaders() }),
        fetch(`${getApiUrl()}/api/analytics/user/sales-trend?year=${filterYear}`, { headers: getAuthHeaders() }),
        fetch(`${getApiUrl()}/api/analytics/user/leaderboard?month=${filterMonth}&year=${filterYear}`, { headers: getAuthHeaders() }),
        fetch(`${getApiUrl()}/api/analytics/user/sales-by-product?month=${filterMonth}&year=${filterYear}`, { headers: getAuthHeaders() }),
      ]);

      const [overviewData, trendData, leaderboardData, productData] = await Promise.all([
        overviewRes.json(),
        trendRes.json(),
        leaderboardRes.json(),
        productRes.json(),
      ]);

      if (overviewData.success) setOverview(overviewData.data);
      if (trendData.success) setSalesTrend(trendData.data.monthly);
      if (leaderboardData.success) setLeaderboard(leaderboardData.data);
      if (productData.success) setSalesByProduct(productData.data.products);

    } catch (err) {
      console.error('Fetch analytics error:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filterYear, filterMonth]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-dark-text">My Performance</h1>
          <p className="text-gray-600 dark:text-dark-muted mt-1">
            {overview?.user?.group || 'N/A'} • {overview?.user?.role || 'N/A'}
          </p>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2">
          <StyledDropdown
            options={months.map(m => ({ value: m.value, label: m.label }))}
            value={filterMonth}
            onChange={(val) => setFilterMonth(parseInt(val))}
            placeholder="Select Month"
            icon={Calendar}
            className="min-w-[150px]"
          />
          <StyledDropdown
            options={years.map(y => ({ value: y, label: y.toString() }))}
            value={filterYear}
            onChange={(val) => setFilterYear(parseInt(val))}
            placeholder="Select Year"
            className="min-w-[120px]"
          />
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Overview Cards */}
      {overview && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="neo-raised p-4 transition-colors duration-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                <DollarSign size={24} className="text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-dark-muted">My Sales</p>
                <p className="text-xl font-bold text-gray-900 dark:text-dark-text">
                  {formatCurrency(overview.currentMonth.sales)}
                </p>
              </div>
            </div>
          </div>

          <div className="neo-raised p-4 transition-colors duration-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Target size={24} className="text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-dark-muted">My Commission</p>
                <p className="text-xl font-bold text-gray-900 dark:text-dark-text">
                  {formatCurrency(overview.currentMonth.commission)}
                </p>
              </div>
            </div>
          </div>

          <div className="neo-raised p-4 transition-colors duration-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Award size={24} className="text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-dark-muted">My Rank</p>
                <p className="text-xl font-bold text-gray-900 dark:text-dark-text">
                  #{overview.rank || '-'} 
                  <span className="text-sm font-normal text-gray-500 dark:text-dark-muted">
                    {overview.totalInGroup > 0 ? ` of ${overview.totalInGroup}` : ''}
                  </span>
                </p>
              </div>
            </div>
          </div>

          <div className="neo-raised p-4 transition-colors duration-200">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${
                overview.growthPercent >= 0 ? 'bg-green-100' : 'bg-red-100'
              }`}>
                {overview.growthPercent >= 0 ? (
                  <TrendingUp size={24} className="text-green-600" />
                ) : (
                  <TrendingDown size={24} className="text-red-600" />
                )}
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-dark-muted">vs Last Month</p>
                <p className={`text-xl font-bold ${
                  overview.growthPercent >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {overview.growthPercent >= 0 ? '+' : ''}{overview.growthPercent}%
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sales Trend Chart */}
      <div className="neo-raised p-6 mb-6 transition-colors duration-200">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-dark-text mb-4">My Monthly Sales ({filterYear})</h2>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={salesTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
              <XAxis 
                dataKey="monthName" 
                tick={{ fontSize: 12, fill: axisColor }} 
                stroke={axisColor}
              />
              <YAxis 
                tick={{ fontSize: 12, fill: axisColor }} 
                tickFormatter={(value) => formatCurrency(value)}
                stroke={axisColor}
              />
              <Tooltip 
                formatter={(value) => [formatCurrencyFull(value), 'Sales']}
                labelStyle={{ fontWeight: 'bold' }}
                contentStyle={{ 
                  backgroundColor: isDarkMode ? '#1e293b' : '#fff',
                  border: `1px solid ${isDarkMode ? '#334155' : '#e5e7eb'}`,
                  color: isDarkMode ? '#e2e8f0' : '#111827'
                }}
              />
              <Line 
                type="monotone" 
                dataKey="sales" 
                stroke="#00837e" 
                strokeWidth={2}
                dot={{ fill: '#00837e', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Leaderboard & Sales by Product */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Group Leaderboard */}
        <div className="neo-raised p-6 transition-colors duration-200">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-dark-text mb-1">
            <Users className="inline-block mr-2 text-primary-500" size={20} />
            Group Leaderboard
          </h2>
          <p className="text-sm text-gray-500 dark:text-dark-muted mb-4">{leaderboard?.groupName || 'N/A'}</p>
          
          {leaderboard?.leaderboard?.length > 0 ? (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {leaderboard.leaderboard.map((member) => (
                <div 
                  key={member.userId} 
                  className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                    member.isCurrentUser 
                      ? 'bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800' 
                      : 'hover:bg-gray-50 dark:hover:bg-slate-700/50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${
                      member.rank === 1 ? 'bg-yellow-100 text-yellow-800' :
                      member.rank === 2 ? 'bg-gray-200 text-gray-800' :
                      member.rank === 3 ? 'bg-orange-100 text-orange-800' :
                      'bg-gray-50 dark:bg-slate-800/50 text-gray-600 dark:text-dark-muted'
                    }`}>
                      {member.rank}
                    </span>
                    <div>
                      <p className={`font-medium ${member.isCurrentUser ? 'text-primary-700 dark:text-primary-400' : 'text-gray-900 dark:text-dark-text'}`}>
                        {member.isCurrentUser ? '★ ' : ''}{member.name}
                        {member.isCurrentUser && <span className="text-xs ml-1">(You)</span>}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-dark-muted">{typeof member.role === 'string' ? member.role : member.role?.name || 'N/A'}</p>
                    </div>
                  </div>
                  <span className={`font-semibold ${member.isCurrentUser ? 'text-primary-700 dark:text-primary-400' : 'text-gray-900 dark:text-dark-text'}`}>
                    {formatCurrency(member.totalSales)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 dark:text-dark-muted text-center py-8">No data available</p>
          )}
        </div>

        {/* Sales by Product */}
        <div className="neo-raised p-6 transition-colors duration-200">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-dark-text mb-4">
            <Package className="inline-block mr-2 text-purple-500" size={20} />
            Sales by Product
          </h2>
          
          {salesByProduct.length > 0 ? (
            <>
              <div className="h-48 mb-4">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={salesByProduct}
                      dataKey="totalSales"
                      nameKey="productName"
                      cx="50%"
                      cy="50%"
                      outerRadius={70}
                      label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {salesByProduct.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value) => formatCurrencyFull(value)}
                      contentStyle={{ 
                        backgroundColor: isDarkMode ? '#1e293b' : '#fff',
                        border: `1px solid ${isDarkMode ? '#334155' : '#e5e7eb'}`,
                        color: isDarkMode ? '#e2e8f0' : '#111827'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              
              <div className="space-y-2">
                {salesByProduct.map((product, index) => (
                  <div key={product.productId} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <span className="text-sm text-gray-700 dark:text-dark-text">{product.productName}</span>
                    </div>
                    <span className="text-sm font-medium text-gray-900 dark:text-dark-text">
                      {formatCurrencyFull(product.totalSales)}
                    </span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-gray-500 dark:text-dark-muted text-center py-8">No sales this period</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserAnalytics;








